/**
 * Converts image data string into Blob object.
 *
 * @param {string} image - Image data base64 string
 * @returns {Object} Image Blob object
 */
export function convertImageFromBase64toBlob(image) {
    const logoBase64 = image.split('base64,')[1]
    const binaryString = atob(logoBase64)
    const binaryArray = new Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
        binaryArray[i] = binaryString.charCodeAt(i)
    }
    const byteArray = new Uint8Array(binaryArray)
    return new Blob([byteArray], { type: 'image/png' })
}

/**
 * Creates a in memory storage provider for the cached image fetch
 *
 * @return {object} - the storage provider
 * @param {number} - the storage limit number
 */
export const createInMemoryStorageProvider = (limit = 20) => {
    const store = new Map()

    const save = (id, value) => {
        if (store.size >= limit) {
            const firstElement = store.keys().next().value
            store.delete(firstElement)
        }
        return store.set(id, value)
    }

    return {
        read: id => store.get(id),
        save,
        remove: id => store.delete(id),
        createKey: id => id,
        getAllKeys: () => Array.from(store.keys())
    }
}

/**
 * Creates a storage provider using the  for the cached image fetch
 *
 * @return {object} - the storage provider
 */
export const createSessionStorageProvider = dbName => {
    if (!window.sessionStorage || !window.sessionStorage.getItem) {
        return createInMemoryStorageProvider()
    }

    const dbRegex = new RegExp(`^${dbName}--`)

    return {
        read: id => window.sessionStorage.getItem(id),
        save: (id, value) => window.sessionStorage.setItem(id, value),
        remove: id => window.sessionStorage.removeItem(id),
        createKey: id => `${dbName}--${id}`,
        getAllKeys: () => Object.keys(window.sessionStorage).filter(str => dbRegex.test(str))
    }
}

function readDataAsURL(data) {
    return new Promise((resolve, reject) => {
        const fileReader = new FileReader()
        fileReader.readAsDataURL(data)
        fileReader.onloadend = () => {
            resolve(fileReader.result)
        }
        fileReader.onerror = err => reject(err)
    })
}

/**
 * Creates a cached image-fetch mechanism. The image is stored in the provided storageProvider
 *
 * @param {object} storageProvider - storage provider for caching the images - default is in memory cache
 * @return {object} -
 */
export const createImageCache = (storageProvider = createInMemoryStorageProvider()) => {
    const loaderRegister = {}

    const isLoading = imageId => !!loaderRegister[imageId]

    const loadImage = async (fetchImage, imageId) => {
        try {
            const result = await fetchImage(imageId)
            if (!result?.data) {
                return null
            }
            const imageData = await readDataAsURL(result.data)
            storageProvider.save(storageProvider.createKey(imageId), imageData)
            return imageData
        } catch (error) {
            console.warn('error fetching image:', error)
            throw error
        }
    }

    const getImage = async (fetchImage, imageId) => {
        let cachedImage = null

        cachedImage = storageProvider.read(storageProvider.createKey(imageId))

        if (isLoading(imageId)) {
            return await loaderRegister[imageId]
        } else if (!cachedImage) {
            try {
                loaderRegister[imageId] = loadImage(fetchImage, imageId)
                const loadedImage = await loaderRegister[imageId]
                return loadedImage
            } finally {
                delete loaderRegister[imageId]
            }
        } else {
            return cachedImage
        }
    }

    const deleteCachedImage = imageId => {
        storageProvider.remove(storageProvider.createKey(imageId))
    }

    const cleanImageCache = imageList => {
        const imageIdMap = imageList.reduce((idMap, id) => {
            idMap[storageProvider.createKey(id)] = true
            return idMap
        }, {})
        const blankKey = storageProvider.createKey('')
        storageProvider.getAllKeys().forEach(key => {
            if (!imageIdMap[key] && key.includes(blankKey)) {
                storageProvider.remove(key)
            }
        })
    }

    return {
        cleanImageCache,
        deleteCachedImage,
        getImage
    }
}
